import { Alert, Box, Button, Grid, TextField } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import AxiosInstance from 'src/shared/services/axios/interceptor';

type StripeConnectModalProps = {
  stripeConnectModal: boolean;
  setStripeConnectModal: (kivraModal: boolean) => void;
  stripeAccount?: string;
  invoiceGroupId: number;
  refetchInvoiceGroup: () => void;
};

export const StripeConnectModal = (props: StripeConnectModalProps) => {
  const { stripeConnectModal, setStripeConnectModal, stripeAccount, invoiceGroupId, refetchInvoiceGroup } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>(false);
  const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState<boolean>(false);

  const stripeConnect = async () => {
    const result = await AxiosInstance.get(`Stripe/CreateAccount?invoiceGroupId=${invoiceGroupId}`);
    if (isMounted()) window.location = result.data;
  };

  const stripeDisconnect = async () => {
    setLoading(true);
    try {
      const res = await AxiosInstance.put(`Stripe/Cancel?invoiceGroupId=${invoiceGroupId}`);
      ConsoleHelper.log('Stripe disconnection result', res);
      if (isMounted()) {
        refetchInvoiceGroup();
        setStripeConnectModal(false);
      }
    } catch (error) {
      if (isMounted()) {
        ConsoleHelper.log('Stripe disconnection failed', error);
        toast.error(<>{t('invoice:adminInvoiceGroupDetailPage.stripeDisconnectionFailed')}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  return (
    <>
      <GenericDialog
        maxWidth='sm'
        disableBackdropClosing
        dialogActions={
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Box>
              <Button variant='outlined' color='error' disabled={!stripeAccount} disableElevation onClick={() => setConfirmDisconnectOpen(true)}>
                {t('common:button.removeConnection')}
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant='outlined' color='primary' disableElevation onClick={() => setStripeConnectModal(false)}>
                {t('common:button.cancel')}
              </Button>
              <Button autoFocus variant='contained' color='primary' disabled={!!stripeAccount} disableElevation onClick={() => stripeConnect()}>
                {t('invoice:adminInvoiceGroupDetailPage.connect')}
              </Button>
            </Box>
          </Box>
        }
        open={stripeConnectModal}
        dialogTitle={t('invoice:adminInvoiceGroupDetailPage.connectStripe')}
        dialogContent={
          <Grid container spacing={3}>
            <LoadingOverlay loading={loading} />
            {stripeAccount === null ||
              (stripeAccount === undefined && (
                <Grid item xs={12} p={0} mb={2}>
                  <Alert
                    icon={false}
                    severity='info'
                    sx={{
                      '& .MuiAlert-message': { width: '100% !important' },
                      fontWeight: 500,
                      m: -2,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {t('invoice:adminInvoiceGroupDetailPage.connectStripeDescription')}
                  </Alert>
                </Grid>
              ))}

            {stripeAccount && (
              <Grid item xs={12}>
                <TextField
                  label={t('invoice:adminInvoiceGroupDetailPage.stripeAccountNumberLabel')}
                  disabled
                  value={stripeAccount}
                  fullWidth
                  size='small'
                />
              </Grid>
            )}
          </Grid>
        }
        onClose={() => setStripeConnectModal(false)}
        onSave={() => {
          setStripeConnectModal(false);
        }}
      />
      <DeleteConfirmationDialog
        open={confirmDisconnectOpen}
        title={t('invoice:adminInvoiceGroupDetailPage.stripeDisconnectConfirmTitle')}
        value=''
        modaltext={t('invoice:adminInvoiceGroupDetailPage.stripeDisconnectConfirmText')}
        onClose={(value) => {
          if (value) {
            setConfirmDisconnectOpen(false);
            stripeDisconnect();
          } else {
            setConfirmDisconnectOpen(false);
          }
        }}
      />
    </>
  );
};
